import type { components, paths } from './generated'

/**
 * The console's one way of talking to its BFF.
 *
 * Injectable rather than a module singleton, because tests need a client
 * pointed at a fake and a singleton would make that a global mutation. Nothing
 * here reads or writes storage: the session lives in an HttpOnly cookie the
 * browser sends on its own, and a token this code could touch is a token it
 * could leak.
 */

export type Session =
  paths['/api/session']['get']['responses']['200']['content']['application/json']
export type Problem = components['schemas']['Problem']
export type ProblemCode = components['schemas']['ProblemCode']
export type ApplicationPage = components['schemas']['ApplicationPage']
export type ApplicationSummary = components['schemas']['ApplicationSummary']
export type AggregateCoverage = components['schemas']['AggregateCoverage']

/** What the Apps table may ask for. Everything else stays at its default. */
export interface ApplicationQuery {
  readonly search?: string
  readonly collection?: string
  readonly freshness?: string
  readonly category?: string
  readonly sort?: string
  readonly direction?: string
  readonly cursor?: string
}

/** A response the console understood and can act on. */
export class ApiProblem extends Error {
  readonly status: number
  readonly code: ProblemCode | 'unknown'
  readonly traceId: string | undefined

  constructor(status: number, code: ProblemCode | 'unknown', traceId?: string) {
    // The message is for a developer reading a stack trace. What a person sees
    // is chosen by the component from `code`, never from this string.
    super(`console api: ${String(status)} ${code}`)
    this.name = 'ApiProblem'
    this.status = status
    this.code = code
    this.traceId = traceId
  }
}

export interface ClientOptions {
  /** Where the API lives. Same origin in the browser; a full origin in tests. */
  readonly baseUrl?: string
  /** Overridable so tests need no network and no global patching. */
  readonly fetch?: typeof globalThis.fetch
  /** How long any one request may take before it is abandoned. */
  readonly timeoutMs?: number
}

/**
 * Ten seconds. Long enough for a cold Lambda plus HOR-223, short enough that a
 * hung dependency becomes a visible error rather than a page that never
 * settles. A request with no timeout is a spinner with no end.
 */
const DEFAULT_TIMEOUT_MS = 10_000

export class ConsoleClient {
  readonly #baseUrl: string
  readonly #fetch: typeof globalThis.fetch
  readonly #timeoutMs: number

  constructor(options: ClientOptions = {}) {
    this.#baseUrl = options.baseUrl ?? ''
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis)
    this.#timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  }

  /** Who the browser is, or that it is nobody. */
  async session(signal?: AbortSignal): Promise<Session> {
    return this.#request<Session>('GET', '/api/session', signal === undefined ? {} : { signal })
  }

  /**
   * Ends the session and returns where to go next.
   *
   * The caller navigates with `window.location.assign`. A redirect cannot do
   * it: this is a `fetch`, and `fetch` follows a cross-origin redirect itself
   * rather than turning it into a top-level navigation, so Cognito would never
   * see the browser and its own cookie would survive to sign the person
   * straight back in.
   */
  async logout(csrfToken: string, signal?: AbortSignal): Promise<{ logout_url: string }> {
    return this.#request<{ logout_url: string }>(
      'POST',
      '/api/auth/logout',
      signal === undefined ? { csrfToken } : { signal, csrfToken },
    )
  }

  /** One page of the fleet's applications, validated by the BFF. */
  async applications(query: ApplicationQuery, signal?: AbortSignal): Promise<ApplicationPage> {
    const params = new URLSearchParams()
    for (const [name, value] of Object.entries(query)) {
      if (typeof value === 'string' && value !== '') {
        params.set(name, value)
      }
    }
    const search = params.toString()
    return this.#request<ApplicationPage>(
      'GET',
      search === '' ? '/api/applications' : `/api/applications?${search}`,
      signal === undefined ? {} : { signal },
    )
  }

  async #request<T>(
    method: string,
    path: string,
    options: { signal?: AbortSignal; csrfToken?: string },
  ): Promise<T> {
    // Two signals: the caller's, and our own timeout. AbortSignal.any means
    // whichever fires first wins, so a component unmounting and a slow BFF are
    // both handled without either cancelling the other's cleanup.
    const timeout = AbortSignal.timeout(this.#timeoutMs)
    const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout

    const headers: Record<string, string> = { Accept: 'application/json' }
    if (options.csrfToken !== undefined) {
      headers['X-CSRF-Token'] = options.csrfToken
    }

    const response = await this.#fetch(`${this.#baseUrl}${path}`, {
      method,
      headers,
      // The session cookie is HttpOnly, so it travels only if credentials are
      // included. Without this every request is anonymous and the console
      // looks permanently signed out.
      credentials: 'include',
      signal,
      // Never cached. Responses are per-principal, and a cached /api/session is
      // one person's identity shown to the next.
      cache: 'no-store',
    })

    if (!response.ok) {
      throw await problemOf(response)
    }
    return (await response.json()) as T
  }
}

/**
 * Builds an ApiProblem from a response.
 *
 * A body that is missing, malformed or not a problem at all still produces a
 * usable error. The status is what the console acts on; the body only refines
 * it. Anything else and a gateway-produced 502 - which carries no problem body,
 * because API Gateway wrote it and not us - would surface as a parse failure
 * instead of an outage.
 */
async function problemOf(response: Response): Promise<ApiProblem> {
  let code: ProblemCode | 'unknown' = 'unknown'
  let traceId: string | undefined

  try {
    const body: unknown = await response.json()
    if (body !== null && typeof body === 'object') {
      const parsed = body as Partial<Problem>
      if (typeof parsed.code === 'string') {
        code = parsed.code
      }
      if (typeof parsed.trace_id === 'string') {
        traceId = parsed.trace_id
      }
    }
  } catch {
    // Deliberately swallowed. There is nothing to add: the status already says
    // what happened, and a parse error here would replace a real outage with a
    // message about JSON.
  }

  return new ApiProblem(response.status, code, traceId)
}
